import React, { ForwardRefRenderFunction, forwardRef, useMemo, useState } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums } from "models/enums";
import { ReceiptFormTypes } from "models/formTypes";
import { FormikProps } from "formik";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { ReceiptDetailsFormContainerProps } from "models/propTypes/receiptPropTypes";
import { FormFieldConstants, TodoConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region  components
import { useDimensionMaps, useNavigationHistory } from "components/common";
import ReceiptDetailsForm from "./detailsForm";
import useDetailsFormHelper from "./helpers/useDetailsFormHelper";
import useEditableState from "../useReadOnlyState";
import usePaymentMethods from "../paymentMethods/forms/usePaymentMethods";
import { useReceiptService } from "serviceHooks";
//#endregion
import { DimensionHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";

const ReceiptDetailsFormContainer: ForwardRefRenderFunction<FormHandles, ReceiptDetailsFormContainerProps> = (
  props,
  ref
) => {
  const receiptService = useReceiptService();
  const formRef = React.useRef<FormHandles>(null);

  React.useImperativeHandle(ref, () =>
    formRef.current
      ? {
          ...formRef.current,
        }
      : ({} as FormHandles)
  );

  const { getRouteStateValue, removeRouteStateValue } = useNavigationHistory();

  const { receipt } = props;

  const [currency, setCurrency] = useState<string>(receipt.currency ?? "");
  const [dimensionAmount, setDimensionAmount] = useState<number>(receipt.amount);
  const [valuesFromTodoForm, setValuesFromTodoForm] = useState<ReceiptFormTypes.Todo>();

  const { receiptDetailsValidationScheme } = useDetailsFormHelper();

  const receiptCategories =
    useSelector((x: AppState) => x.auth.selectedCompany.receiptCategories ?? [])?.map((item) => ({
      id: item.id,
      value: item.name ?? " ",
      group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      data: item,
    })) ?? [];

  const { receiptPaymentMethods, addPaymentAction } = usePaymentMethods(receipt);

  const { dimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);
  const isReadOnly = useEditableState(receipt.state, receipt.createdByPersonId);

  const [attachments, setAttachments] = React.useState<WintSuperkollAPIModel.ReceiptAttachment[]>();

  React.useEffect(() => {
    if (receipt.attachments)
      Promise.all(
        receipt.attachments.map((attachment) => {
          return receiptService.getAttachment(receipt.id, attachment.id);
        })
      ).then((val) => setAttachments(val));
  }, [receipt]);

  const initialValues = useMemo(
    (): ReceiptFormTypes.Details => ({
      createdBy: receipt.createdByPersonName ?? "",
      receiptCategoryId: receipt.receiptCategoryId!,
      receiptCategory: receipt.categoryName || undefined,
      receiptCategoryType: receiptCategories.find((r) => r.id === receipt.receiptCategoryId)?.data.categoryType,
      intent: receipt.intent ?? "",
      otherExplanation: receipt.intent ?? "",
      persons: receipt.persons?.map((p) => ({ ...p, id: UtilsHelper.getUniqueId() })) ?? [],
      amount: receipt.amount,
      currency: receipt.currency,
      purchaseDate: receipt.dateTime,
      paymentMethodId: receipt.paymentMethodId ?? OTHER_PAYMENT_METHOD_ID,
      paymentMethod: receipt.paymentMethodName || undefined,
      paymentMethodType: receipt.paymentMethodType,
      paymentStatus: receiptService.getPaymentStatusText(receipt.paymentState),
      recipientAccount: receipt.recipientAccount ?? "-",
      paymentDate:
        receipt.paymentMethodType !== WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense
          ? receipt.dateTime
          : receipt.paidOut
            ? receipt.paymentDate
            : undefined,
      dimensions: dimensions ? DimensionHelper.getValidList(receipt.dimensionList ?? [], dimensions) : [],
      showDimensions: dimensions && dimensions.length > 0,
      filesToUpload: (attachments ?? receipt.attachments) as UploadedFile[],
      canPayEmployee: receipt.canPayEmployee,
      isManualPayment: receipt.hasMissingPaymentDetails,
      personNumber: (receipt.personnumber ?? "").replace("-", ""),
      bankAccount: "",
      clearingNumber: "",
      internalNote: receipt.comment ?? "",
      supplierName: receipt.supplierName,
      isOtherPaymentMethod: receipt.isOtherPaymentMethod,
      otherPaymentMethodExplanation: receipt.otherPaymentMethodExplanation,
      ...(valuesFromTodoForm ?? {}),
    }),
    [receipt, attachments, valuesFromTodoForm, dimensions]
  );

  React.useEffect(() => {
    const state = getRouteStateValue(TodoConstants.RouteTodoSelectionKey);

    if (state) {
      setValuesFromTodoForm(state);
      removeRouteStateValue(TodoConstants.RouteTodoSelectionKey);
    }
  }, []);

  //#region event handlers
  const onAmountsChange = (name: string, value: any, formikProps: FormikProps<ReceiptFormTypes.Details>) => {
    setDimensionAmount(value);
  };

  const onCurrencyChange = (name: string, value: any, formikProps: FormikProps<ReceiptFormTypes.Details>) => {
    setCurrency(value);
  };

  const onPurchaseDateChange = (name: string, value: any, formikProps: FormikProps<ReceiptFormTypes.Details>) => {
    const { values, setFieldValue } = formikProps;
    if (values.paymentMethodType !== WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense)
      setFieldValue(FormFieldConstants.Receipt.Details.PaymentDate, values.purchaseDate);
  };

  const onPaymentMethodSelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<ReceiptFormTypes.Details>
  ) => {
    const { values, setFieldValue } = formikProps;
    const selectedPaymentMethod: WintSuperkollAPIModel.ReceiptPaymentMethod = item.data;

    if (selectedPaymentMethod.id === OTHER_PAYMENT_METHOD_ID) {
      setFieldValue(FormFieldConstants.Receipt.Details.IsOtherPaymentMethod, true);
    } else {
      setFieldValue(FormFieldConstants.Receipt.Details.IsOtherPaymentMethod, false);
    }

    if (values.paymentMethodType !== selectedPaymentMethod.type) {
      setFieldValue(FormFieldConstants.Receipt.Details.PaymentMethodType, selectedPaymentMethod.type);

      setFieldValue(
        FormFieldConstants.Receipt.Details.PaymentDate,
        selectedPaymentMethod.type !== WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense
          ? values.purchaseDate
          : undefined
      );
    }

    if (receiptPaymentMethods.find((p) => p.id === item.id) === undefined) {
      receiptPaymentMethods.push({
        ...item,
        data: { type: WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense },
      });
    }
  };

  const onReceiptCategorySelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<ReceiptFormTypes.Details>
  ) => {
    const { values, setFieldValue } = formikProps;
    const selectedReceiptCategory: WintSuperkollAPIModel.ReceiptCategory = item.data;
    if (values.receiptCategoryType !== selectedReceiptCategory.categoryType) {
      setFieldValue(FormFieldConstants.Receipt.Details.ReceiptCategoryType, selectedReceiptCategory.categoryType);
    }
  };

  // #endregion

  const isDisabledForWintCardReceiptDraft = (values: ReceiptFormTypes.Details) => {
    return values.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard;
  };

  const canPayEmployeeSelf = (values: ReceiptFormTypes.Details) =>
    values.paymentMethodType !== WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense || values.canPayEmployee;

  const isRepresentation = (values: ReceiptFormTypes.Details) => {
    return (
      (values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.Representation &&
        values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) ||
      (receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft && !values.receiptCategoryId)
    );
  };

  const isOtherPaymentMethod = (values: ReceiptFormTypes.Details) => {
    return values.isOtherPaymentMethod === true;
  };


  return (
    <ReceiptDetailsForm
      ref={formRef}
      validationSchema={receiptDetailsValidationScheme(
        receipt.state === WintSuperkollAPIModel.ReceiptStateApiModel.Draft &&
          receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
      )}
      defaultValues={initialValues}
      receipt={receipt}
      currency={currency}
      dimensionAmount={dimensionAmount}
      amountsChangeHandler={onAmountsChange}
      currencyChangeHandler={onCurrencyChange}
      receiptCategories={receiptCategories}
      paymentMethods={receiptPaymentMethods}
      isReadOnly={isReadOnly}
      onFormValidationChange={props.onFormValidationChange}
      paymentAddActions={addPaymentAction}
      purchaseDateChangeHandler={onPurchaseDateChange}
      paymentMethodSelectHandler={onPaymentMethodSelect}
      receiptCategorySelectHandler={onReceiptCategorySelect}
      fileViewer={props.fileViewer}
      isDisabledForWintCardReceiptDraft={isDisabledForWintCardReceiptDraft}
      canPayEmployeeSelf={canPayEmployeeSelf}
      isRepresentation={isRepresentation}
      isOtherPaymentMethod={isOtherPaymentMethod}
    />
  );
};

export default forwardRef(ReceiptDetailsFormContainer);
